import React, { SelectHTMLAttributes, ReactNode, forwardRef } from 'react';
import classNames from 'classnames';
import Select from './Select';
import FormControlWrapper from './FormControlWrapper';
import { InputVariant } from './InputVariant';
import controlHintIds from './controlHintIds';

export interface DropDownControlProps extends SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  label: string;
  errorId?: string;
  errorMessage?: string;
  success?: boolean;
  hint?: string;
  labelHint?: ReactNode;
  note?: ReactNode;
  variant?: InputVariant;
  requiredLabel?: ReactNode;
}

/**
 * The Dropdown Control based on [The USWDS Dropdown component](https://designsystem.digital.gov/components/form-controls/#dropdown)
 *
 * note: Configured `requiredLabel` text is appended to the labelhint if required attribute is true.
 */
const DropdownControl = forwardRef<HTMLSelectElement, DropDownControlProps>(
  (
    {
      className,
      id,
      label,
      labelHint,
      hint,
      note,
      errorMessage,
      variant,
      'aria-describedby': ariaDescribedBy,
      required,
      requiredLabel,
      ...rest
    }: DropDownControlProps,
    ref,
  ): JSX.Element => {
    const { labelHintId, hintId, errorId, describedBy } = controlHintIds(
      id,
      labelHint,
      hint,
      errorMessage,
      undefined,
      ariaDescribedBy,
    );
    return (
      <FormControlWrapper
        id={id}
        label={label}
        labelHint={labelHint}
        labelHintId={labelHintId}
        hint={hint}
        hintId={hintId}
        note={note}
        errorId={errorId}
        errorMessage={errorMessage}
        required={required}
        requiredLabel={requiredLabel}
      >
        <Select
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
          aria-describedby={describedBy}
          id={id}
          className={classNames('dropdown-control__select', className)}
          ref={ref}
          errorId={errorId}
          variant={variant}
          aria-required={required}
          required={required}
        />
      </FormControlWrapper>
    );
  },
);

export default DropdownControl;
